import { useEffect, useRef } from 'react'
import { motion, useAnimation, AnimatePresence } from 'framer-motion'
import { useUnit } from 'effector-react'
import { $specialOfferInfo } from '@/entities/payments'

type SpecialOfferAnimationProps = {
  onComplete: () => void
}

export const SpecialOfferAnimation = ({ onComplete }: SpecialOfferAnimationProps) => {
  const specialOfferInfo = useUnit($specialOfferInfo)
  const controls = useAnimation()
  const flashControls = useAnimation()
  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
      controls.stop()
      flashControls.stop()
    }
  }, [controls, flashControls])

  useEffect(() => {
    const runAnimation = async () => {
      if (!specialOfferInfo?.photo_url) return

      await controls.set({
        opacity: 0,
        scale: 0.6,
        rotate: -15,
        x: 0,
        y: 100,
        filter: 'blur(10px) brightness(160%)',
      })

      await controls.start({
        opacity: 1,
        x: [0, 80, -40, 0],
        y: [100, -50, -20, -10],
        rotate: [-15, 10, -5, 0],
        scale: [0.6, 1.2, 1.1, 1],
        filter: ['blur(10px)', 'blur(3px)', 'blur(0px) brightness(100%)'],
        transition: {
          duration: 1,
          ease: 'easeInOut',
        },
      })

      await Promise.all([
        controls.start({
          opacity: 0,
          scale: 2,
          y: -200,
          rotate: 25,
          transition: {
            duration: 0.25,
            ease: 'easeIn',
          },
        }),
        flashControls.start({
          opacity: 1,
          transition: { duration: 0.1 },
        }),
      ])

      await flashControls.start({
        opacity: 0,
        transition: { duration: 0.3 },
      })

      if (isMounted.current) onComplete()
    }

    runAnimation()

    return () => {
      controls.stop()
      flashControls.stop()
    }
  }, [specialOfferInfo, controls, flashControls, onComplete])
  if (!specialOfferInfo?.photo_url) {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        key="flash-overlay"
        initial={{ opacity: 0 }}
        animate={flashControls}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 1)',
          zIndex: 1001,
          pointerEvents: 'none',
        }}
      />

      <motion.div
        key="special-offer-container"
        animate={controls}
        style={{
          position: 'fixed',
          top: '30%',
          left: '10%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          pointerEvents: 'none',
          perspective: '1000px',
        }}
      >
        <motion.img
          src={specialOfferInfo.photo_url}
          alt="Special Offer"
          style={{
            maxWidth: 'min(80vw, 400px)',
            filter: 'drop-shadow(0 8px 25px rgba(0, 0, 0, 0.4))',
            transformOrigin: 'center',
            borderRadius: '8px',
            willChange: 'transform, opacity, filter',
          }}
          draggable={false}
        />
      </motion.div>
    </AnimatePresence>
  )
}
